import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import styled from 'styled-components';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { tableCellClasses } from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MuiButton from '@mui/material/Button';
import { toast } from 'react-toastify';
import IconosGenerales from '../elementos/iconos/IconosGenerales';
import { UsuarioContext } from '../providers/personaContext';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { fetchPersonasVacaciones, asignarCalendarioLaboralMasivo } from '../../peticiones_API/PeticionesVacaciones';
import { fetchCalendariosLaborales } from '../../peticiones_API/PeticionesCalendariosLaborales';
import { fetchCalendariosLaboralesColab } from '../../peticiones_API/PeticionesCalendariosLaboralesColab';
import { fetchEmpresasColaboradorasData } from '../../peticiones_API/PeticionesEmpresasColaboradoras';
import { fetchDepartamentosData, fetchNegociosData } from '../../peticiones_API/PeticionesGestionPersonasPersonas';

/**
 * GestorAsignacionVacaciones — asignación masiva de calendario laboral a personas
 * internas y colaboradoras. Extraído (Fase 1) de las pestañas de Vacaciones
 * (?tabPersonas 2 y 3) de GestionPersonasPersonas.js a /panel/vacaciones/asignacion.
 *
 * DS: MUI + shadcn v1 (src/components/ui/*), NO ui-v2 (decisión Iban, Fase 1).
 * Los sub-tabs internos (subTab 'interno'|'colab') sustituyen a los índices
 * tabVacacionesIntIndex/ColabIndex del origen.
 *
 * Contrato de asignarCalendarioLaboralMasivo (INTACTO, dos fases):
 *   1) (ids, calendarioId|null, tipoTab)            -> preview {recuento, fuera_de_ambito} (no escribe)
 *   2) (ids, calendarioId|null, tipoTab, recuento)  -> aplica (el servidor re-verifica/clampa)
 */

const API_URL = process.env.REACT_APP_API_URL;

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    textAlign: 'center !important',
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: 'rgba(0, 60, 102, 0.8)',
        color: 'white',
        padding: '12px 16px',
        fontWeight: 'bold',
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    textAlign: 'center !important',
    '&:nth-of-type(odd)': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '&:nth-of-type(even)': {
        backgroundColor: 'transparent',
    },
    '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const WhiteTableSortLabel = styled(TableSortLabel)(({ theme }) => ({
    color: 'white !important',
    '&.Mui-active': {
        color: 'white !important',
    },
    '&.Mui-active .MuiTableSortLabel-icon': {
        color: 'white !important',
    },
    '& .MuiTableSortLabel-icon': {
        color: 'white !important',
    },
}));

const ContenedorPaginado = styled(Stack)`
    display: flex;
    align-items: center;
    justify-content: center;
    .MuiPaginationItem-root.Mui-selected {
        background-color: rgba(0, 60, 102, 0.8) !important;
        color: #fff !important;
    }
`;

export default function GestorAsignacionVacaciones() {
  const { persona } = useContext(UsuarioContext);
  // SA/Admin/RRHH ven la columna de "editar" (misma regla que el origen). El gate de la
  // RUTA (ProtectedRoute super administrador / recursos humanos) se aplica en App.js (§3.6).
  const rolAdminExtendido = ['super administrador', 'administrador', 'recursos humanos'].includes(persona?.rol?.toLowerCase());

  const [subTab, setSubTab] = useState('interno'); // 'interno' | 'colab'

  // Listas base para los filtros (el origen las cargaba en su efecto principal; aquí el
  // componente carga las suyas — no se arrastran del origen).
  const [departamentos, setDepartamentos] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [empresasColaboradoras, setEmpresasColaboradoras] = useState([]); // DUPLICADO del origen

  const [loadingVacaciones, setLoadingVacaciones] = useState(false);
  const [personasVacaciones, setPersonasVacaciones] = useState([]);
  const [metaVacaciones, setMetaVacaciones] = useState({ total: 0, per_page: 10, current_page: 1 });
  const [busquedaVacaciones, setBusquedaVacaciones] = useState('');
  const [calendariosLaborales, setCalendariosLaborales] = useState([]);
  const [filtroCalendario, setFiltroCalendario] = useState('');
  const [filtroEmpresaVacaciones, setFiltroEmpresaVacaciones] = useState([]);
  const [filtroDepartamentoVacaciones, setFiltroDepartamentoVacaciones] = useState([]);
  const [departamentosVacaciones, setDepartamentosVacaciones] = useState([]);
  const [ordenVacaciones, setOrdenVacaciones] = useState('asc');
  const [ordenarPorVacaciones, setOrdenarPorVacaciones] = useState('nombre');
  const [registrosPorPagVacaciones, setRegistrosPorPagVacaciones] = useState(10);
  const [personasSeleccionadas, setPersonasSeleccionadas] = useState([]);
  const [showAsignarCalendarioModal, setShowAsignarCalendarioModal] = useState(false);
  const [calendarioParaAsignar, setCalendarioParaAsignar] = useState('');
  // Checkpoint del masivo: previewAsignar = {recuento, fuera_de_ambito, tipoTab} o null (diálogo
  // MUI en vez de window.confirm); aplicandoAsignar deshabilita el botón mientras escribe.
  const [previewAsignar, setPreviewAsignar] = useState(null);
  const [aplicandoAsignar, setAplicandoAsignar] = useState(false);

  // Debounce del buscador con useRef (desviación autorizada §3.5): sustituye a
  // window.searchTimeout global del origen. Mismo comportamiento observable.
  const searchTimeoutRef = useRef(null);

  const handleSubTabChange = (event, newValue) => {
    setSubTab(newValue);
  };

  const cargarCalendariosLaborales = useCallback(async (esColab = false) => {
    try {
      const data = esColab ? await fetchCalendariosLaboralesColab() : await fetchCalendariosLaborales();
      setCalendariosLaborales(data || []);
    } catch (error) {
      console.error('Error al cargar calendarios laborales:', error);
    }
  }, []);

  const cargarPersonasVacaciones = useCallback(async (page = 1, search = '', calendarioId = '', perPage = 10, empresaId = '', departamentoId = '', empresasIds = null, empresasColabIds = null, tipoTab = null, departamentosGestionIds = null) => {
    setLoadingVacaciones(true);
    try {
      const params = {
        current_page: page,
        items_per_page: perPage,
        busqueda: search
      };
      if (calendarioId) {
        params.calendario_laboral_id = calendarioId;
      }
      if (empresaId && (Array.isArray(empresaId) ? empresaId.length > 0 : empresaId)) {
        params.negocio = Array.isArray(empresaId) ? empresaId.join(',') : empresaId;
      }
      if (departamentoId && (Array.isArray(departamentoId) ? departamentoId.length > 0 : departamentoId)) {
        params.departamento = Array.isArray(departamentoId) ? departamentoId.join(',') : departamentoId;
      }
      if (empresasIds && empresasIds.length > 0) {
        params.empresas_ids = empresasIds;
      }
      if (empresasColabIds && empresasColabIds.length > 0) {
        params.empresas_colab_ids = empresasColabIds;
      }
      if (tipoTab) {
        params.tipo_tab = tipoTab;
      }
      if (departamentosGestionIds && departamentosGestionIds.length > 0) {
        params.departamentos_gestion_ids = departamentosGestionIds;
      }
      const response = await fetchPersonasVacaciones(params);
      setPersonasVacaciones(response.data);
      setMetaVacaciones(response.meta);
    } catch (error) {
      console.error('Error al cargar personas con vacaciones:', error);
    } finally {
      setLoadingVacaciones(false);
    }
  }, []);

  // Listas base para los filtros (mount).
  useEffect(() => {
    fetchDepartamentosData().then((data) => setDepartamentos(data || [])).catch(() => {});
    fetchNegociosData().then((data) => setNegocios(data || [])).catch(() => {});
  }, []);

  // Carga al montar / al cambiar de sub-tab (adaptado de tabActual -> subTab).
  useEffect(() => {
    setFiltroEmpresaVacaciones([]);
    setFiltroDepartamentoVacaciones([]);

    if (subTab === 'interno') {
      cargarCalendariosLaborales(false);
      // Sin delegación (eliminada): no se acota en cliente; el backend acota por gestionable.
      cargarPersonasVacaciones(1, '', filtroCalendario, registrosPorPagVacaciones, '', '', null, null, 'interno', null);
    } else if (subTab === 'colab') {
      cargarCalendariosLaborales(true);
      cargarPersonasVacaciones(1, '', filtroCalendario, registrosPorPagVacaciones, '', '', null, null, 'colab');
      fetchEmpresasColaboradorasData().then(data => setEmpresasColaboradoras((data || []).filter(e => e.vacaciones))).catch(() => {});
    }
    // Deuda HEREDADA del origen (GestionPersonasPersonas.js:671, mismo warning): el efecto
    // lee los valores ACTUALES de filtroCalendario/registrosPorPagVacaciones sin re-dispararse
    // por ellos. Comportamiento INTENCIONAL (idéntico al origen), no una decisión nueva.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab, cargarPersonasVacaciones, cargarCalendariosLaborales]);

  // Derivar departamentos internos filtrados por negocio seleccionado (solo eje interno).
  useEffect(() => {
    if (subTab !== 'interno' || !departamentos.length) return;

    const empresaIdsSeleccionados = filtroEmpresaVacaciones.map(e => Number(e.Id));
    let depsBase = departamentos.filter(dep => dep.tipo === "interno");
    if (empresaIdsSeleccionados.length > 0) {
      depsBase = depsBase.filter(dep =>
        dep.Negocios?.some(emp => empresaIdsSeleccionados.includes(Number(emp.Id)))
      );
    }
    setDepartamentosVacaciones(depsBase);
  }, [filtroEmpresaVacaciones, departamentos, subTab]);

  const getEmpresasVacacionesParams = () => {
    // Delegación eliminada (código muerto): no se acota en cliente; el backend acota por el
    // eje gestionable (275/276). Solo se conserva `tipoTab` (presentación: qué eje/pestaña).
    const tipoTab = subTab === 'colab' ? 'colab' : 'interno';
    return { empresasIds: null, empresasColabIds: null, tipoTab, deptGestionIds: null };
  };

  const handleChangePageVacaciones = (event, newPage) => {
    const { empresasIds, empresasColabIds, tipoTab, deptGestionIds } = getEmpresasVacacionesParams();
    cargarPersonasVacaciones(newPage, busquedaVacaciones, filtroCalendario, registrosPorPagVacaciones, filtroEmpresaVacaciones.map(e => e.Id), filtroDepartamentoVacaciones.map(d => d.Id), empresasIds, empresasColabIds, tipoTab, deptGestionIds);
  };

  const handleSearchVacaciones = (e) => {
    const value = e.target.value;
    setBusquedaVacaciones(value);
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      const { empresasIds, empresasColabIds, tipoTab, deptGestionIds } = getEmpresasVacacionesParams();
      cargarPersonasVacaciones(1, value, filtroCalendario, registrosPorPagVacaciones, filtroEmpresaVacaciones.map(e => e.Id), filtroDepartamentoVacaciones.map(d => d.Id), empresasIds, empresasColabIds, tipoTab, deptGestionIds);
    }, 500);
  };

  const handleFiltroCalendarioChange = (e) => {
    const value = e.target.value;
    setFiltroCalendario(value);
    const { empresasIds, empresasColabIds, tipoTab, deptGestionIds } = getEmpresasVacacionesParams();
    cargarPersonasVacaciones(1, busquedaVacaciones, value, registrosPorPagVacaciones, filtroEmpresaVacaciones.map(e => e.Id), filtroDepartamentoVacaciones.map(d => d.Id), empresasIds, empresasColabIds, tipoTab, deptGestionIds);
  };

  const handleFiltroEmpresaVacacionesChange = (newValue) => {
    setFiltroEmpresaVacaciones(newValue);
    setFiltroDepartamentoVacaciones([]);
    const { empresasIds, empresasColabIds, tipoTab, deptGestionIds } = getEmpresasVacacionesParams();
    cargarPersonasVacaciones(1, busquedaVacaciones, filtroCalendario, registrosPorPagVacaciones, newValue.map(e => e.Id), [], empresasIds, empresasColabIds, tipoTab, deptGestionIds);
  };

  const handleFiltroDepartamentoVacacionesChange = (newValue) => {
    setFiltroDepartamentoVacaciones(newValue);
    const { empresasIds, empresasColabIds, tipoTab, deptGestionIds } = getEmpresasVacacionesParams();
    cargarPersonasVacaciones(1, busquedaVacaciones, filtroCalendario, registrosPorPagVacaciones, filtroEmpresaVacaciones.map(e => e.Id), newValue.map(d => d.Id), empresasIds, empresasColabIds, tipoTab, deptGestionIds);
  };

  const handleChangeRegistrosPorPagVacaciones = (e) => {
    const value = e.target.value;
    setRegistrosPorPagVacaciones(value);
    const { empresasIds, empresasColabIds, tipoTab, deptGestionIds } = getEmpresasVacacionesParams();
    cargarPersonasVacaciones(1, busquedaVacaciones, filtroCalendario, value, filtroEmpresaVacaciones.map(e => e.Id), filtroDepartamentoVacaciones.map(d => d.Id), empresasIds, empresasColabIds, tipoTab, deptGestionIds);
  };

  const handleSortVacaciones = (columna) => {
    const isAsc = ordenarPorVacaciones === columna && ordenVacaciones === 'asc';
    setOrdenVacaciones(isAsc ? 'desc' : 'asc');
    setOrdenarPorVacaciones(columna);
  };

  const personasVacacionesOrdenadas = [...personasVacaciones].sort((a, b) => {
    let valorA, valorB;
    switch (ordenarPorVacaciones) {
      case 'nombre':
        valorA = a.nombre?.toLowerCase() || '';
        valorB = b.nombre?.toLowerCase() || '';
        break;
      case 'dias_disponibles_normal':
        valorA = a.dias_disponibles_normal ?? a.dias_disponibles;
        valorB = b.dias_disponibles_normal ?? b.dias_disponibles;
        break;
      case 'dias_disponibles_verano':
        valorA = a.dias_disponibles_verano ?? 0;
        valorB = b.dias_disponibles_verano ?? 0;
        break;
      case 'progreso':
        valorA = a.dias_totales > 0 ? (a.dias_usados / a.dias_totales) * 100 : 0;
        valorB = b.dias_totales > 0 ? (b.dias_usados / b.dias_totales) * 100 : 0;
        break;
      default:
        valorA = a.nombre?.toLowerCase() || '';
        valorB = b.nombre?.toLowerCase() || '';
    }
    if (ordenVacaciones === 'asc') {
      return valorA < valorB ? -1 : valorA > valorB ? 1 : 0;
    } else {
      return valorA > valorB ? -1 : valorA < valorB ? 1 : 0;
    }
  });

  const handleSelectAllVacaciones = (event) => {
    if (event.target.checked) {
      setPersonasSeleccionadas(personasVacaciones.map(p => p.Id));
    } else {
      setPersonasSeleccionadas([]);
    }
  };

  const handleSelectPersonaVacaciones = (id) => {
    setPersonasSeleccionadas(prev => {
      if (prev.includes(id)) {
        return prev.filter(pId => pId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleAsignarCalendario = async () => {
    if (personasSeleccionadas.length === 0) {
      toast.warning('Debe seleccionar al menos una persona');
      return;
    }
    try {
      const { tipoTab } = getEmpresasVacacionesParams();
      // FASE 1 — PREVIEW (sin recuento): el servidor devuelve a cuánta gente afectaría y
      // quién queda FUERA de tu ámbito. No escribe nada. El resultado abre el DIÁLOGO de
      // checkpoint (MUI, con estilo).
      const preview = await asignarCalendarioLaboralMasivo(personasSeleccionadas, calendarioParaAsignar || null, tipoTab);
      setPreviewAsignar({ ...preview, tipoTab });
      setShowAsignarCalendarioModal(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al asignar calendario laboral');
    }
  };

  // FASE 2 — APLICAR: el recuento confirmado viaja de vuelta y el servidor lo re-verifica (y
  // clampa por destinatario) antes de escribir — esa lógica NO cambia. El botón se deshabilita
  // mientras la petición está en vuelo -> sin doble escritura.
  const handleConfirmarAsignar = async () => {
    if (!previewAsignar || aplicandoAsignar) return;
    setAplicandoAsignar(true);
    try {
      const result = await asignarCalendarioLaboralMasivo(personasSeleccionadas, calendarioParaAsignar || null, previewAsignar.tipoTab, previewAsignar.recuento);
      toast.success(result.message);
      setPreviewAsignar(null);
      setCalendarioParaAsignar('');
      setPersonasSeleccionadas([]);
      cargarPersonasVacaciones(1, busquedaVacaciones, filtroCalendario, registrosPorPagVacaciones, filtroEmpresaVacaciones.map(e => e.Id), filtroDepartamentoVacaciones.map(d => d.Id), null, null, previewAsignar.tipoTab, null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al asignar calendario laboral');
    } finally {
      setAplicandoAsignar(false);
    }
  };

  const getProgressColor = (pct) => {
    if (pct > 80) return 'bg-red-500';
    if (pct > 50) return 'bg-orange-400';
    return 'bg-green-500';
  };

  return (
    <>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-[rgba(0,60,102,0.9)]">
          Asignación de calendario laboral
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Asigna de forma masiva el calendario laboral a empleados internos y a colaboradores.
        </p>
      </div>

      <div className="border-b border-gray-200 mb-4">
        <Tabs
          value={subTab}
          onChange={handleSubTabChange}
          sx={{
            '& .MuiTab-root': { fontWeight: 'bold', textTransform: 'none', fontSize: '1rem' },
            '& .Mui-selected': { color: '#336385' },
            '& .MuiTabs-indicator': { backgroundColor: '#336385' },
          }}
        >
          <Tab value="interno" icon={<PeopleIcon />} iconPosition="start" label="Personas internas" />
          <Tab value="colab" icon={<GroupsIcon />} iconPosition="start" label="Colaboradores" />
        </Tabs>
      </div>

      {/* ===== UI copiada de GestionPersonasPersonas.js:954-1204 (condiciones traducidas a subTab) ===== */}
      <div>
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <TextField
            size="small"
            placeholder="Buscar por nombre o apellidos..."
            value={busquedaVacaciones}
            onChange={handleSearchVacaciones}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <Autocomplete
            multiple
            size="small"
            sx={{ minWidth: 250 }}
            options={
              subTab === 'colab' ? empresasColaboradoras : negocios
            }
            getOptionLabel={(option) => option.nombre || ''}
            value={filtroEmpresaVacaciones}
            onChange={(e, newValue) => handleFiltroEmpresaVacacionesChange(newValue)}
            renderInput={(params) => (
              <TextField {...params} label={subTab === 'colab' ? "Empresa" : "Negocio"} />
            )}
            isOptionEqualToValue={(option, value) => option.Id === value?.Id}
          />
          {subTab !== 'colab' && (
            <Autocomplete
              multiple
              size="small"
              sx={{ minWidth: 250 }}
              options={departamentosVacaciones}
              getOptionLabel={(option) => option.nombre || ''}
              value={filtroDepartamentoVacaciones}
              onChange={(e, newValue) => handleFiltroDepartamentoVacacionesChange(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Departamento" />
              )}
              isOptionEqualToValue={(option, value) => option.Id === value?.Id}
            />
          )}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Calendario Laboral</InputLabel>
            <Select
              value={filtroCalendario}
              onChange={handleFiltroCalendarioChange}
              label="Calendario Laboral"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="sin_asignar">Sin asignar</MenuItem>
              {calendariosLaborales.map((cal) => (
                <MenuItem key={cal.Id} value={cal.Id}>
                  {cal.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            disabled={personasSeleccionadas.length === 0}
            onClick={() => setShowAsignarCalendarioModal(true)}
            className="bg-[#336385] hover:bg-[#27496d] text-white disabled:bg-gray-300 disabled:text-gray-500"
          >
            Asignar Calendario ({personasSeleccionadas.length})
          </Button>
        </div>

        {loadingVacaciones ? (
          <div className="w-full">
            <div className="h-1 w-full overflow-hidden rounded-full bg-gray-200">
              <div className="h-full w-1/2 animate-pulse rounded-full bg-[#336385]" />
            </div>
          </div>
        ) : (
          <>
            <Paper sx={{ width: '98%', mb: 2 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell padding="checkbox">
                        <Checkbox
                          sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                          indeterminate={personasSeleccionadas.length > 0 && personasSeleccionadas.length < personasVacaciones.length}
                          checked={personasVacaciones.length > 0 && personasSeleccionadas.length === personasVacaciones.length}
                          onChange={handleSelectAllVacaciones}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <WhiteTableSortLabel
                          active={ordenarPorVacaciones === 'nombre'}
                          direction={ordenarPorVacaciones === 'nombre' ? ordenVacaciones : 'asc'}
                          onClick={() => handleSortVacaciones('nombre')}
                        >
                          Persona
                        </WhiteTableSortLabel>
                      </StyledTableCell>
                      <StyledTableCell align="center">Calendario Laboral</StyledTableCell>
                      <StyledTableCell align="center">
                        <WhiteTableSortLabel
                          active={ordenarPorVacaciones === 'dias_disponibles_normal'}
                          direction={ordenarPorVacaciones === 'dias_disponibles_normal' ? ordenVacaciones : 'asc'}
                          onClick={() => handleSortVacaciones('dias_disponibles_normal')}
                        >
                          Disp. Normal
                        </WhiteTableSortLabel>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <WhiteTableSortLabel
                          active={ordenarPorVacaciones === 'dias_disponibles_verano'}
                          direction={ordenarPorVacaciones === 'dias_disponibles_verano' ? ordenVacaciones : 'asc'}
                          onClick={() => handleSortVacaciones('dias_disponibles_verano')}
                        >
                          Disp. Verano
                        </WhiteTableSortLabel>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <WhiteTableSortLabel
                          active={ordenarPorVacaciones === 'progreso'}
                          direction={ordenarPorVacaciones === 'progreso' ? ordenVacaciones : 'asc'}
                          onClick={() => handleSortVacaciones('progreso')}
                        >
                          Progreso
                        </WhiteTableSortLabel>
                      </StyledTableCell>
                      {rolAdminExtendido && <StyledTableCell align="center"></StyledTableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {personasVacacionesOrdenadas.map((p) => {
                      const porcentajeUsado = p.dias_totales > 0
                        ? (p.dias_usados / p.dias_totales) * 100
                        : 0;
                      return (
                        <StyledTableRow key={p.Id}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={personasSeleccionadas.includes(p.Id)}
                              onChange={() => handleSelectPersonaVacaciones(p.Id)}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarImage src={p.foto ? `${API_URL}/api/persona/imagen/${p.foto}` : null} />
                                <AvatarFallback className="bg-[#336385] text-white text-sm">
                                  {p.nombre?.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-sm">
                                {p.nombre} {p.apellidos}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell align="center">
                            <span className={`text-sm ${p.calendario_laboral === 'Sin asignar' ? 'text-red-500' : ''}`}>
                              {p.calendario_laboral}
                            </span>
                          </TableCell>
                          <TableCell align="center">
                            <span className={`text-sm ${(p.dias_disponibles_normal ?? p.dias_disponibles) < 5 ? 'text-red-500' : 'text-green-600'}`}>
                              {p.dias_disponibles_normal ?? p.dias_disponibles} / {p.dias_normal_total ?? p.dias_totales}
                            </span>
                          </TableCell>
                          <TableCell align="center">
                            <span className={`text-sm ${(p.dias_disponibles_verano ?? 0) < 3 ? 'text-red-500' : 'text-orange-500'}`}>
                              {p.dias_disponibles_verano ?? 0} / {p.dias_verano_total ?? 0}
                            </span>
                          </TableCell>
                          <TableCell align="center" sx={{ width: 200 }}>
                            <div className="flex items-center justify-center gap-2">
                              <Progress
                                value={porcentajeUsado}
                                className="w-[70%] h-2 bg-gray-200"
                                indicatorClassName={getProgressColor(porcentajeUsado)}
                              />
                              <span className="text-xs min-w-[35px]">
                                {Math.round(porcentajeUsado)}%
                              </span>
                            </div>
                          </TableCell>
                          {rolAdminExtendido && (
                          <TableCell align="center">
                            <IconosGenerales
                              tabla="true"
                              id="edit"
                              to={subTab === 'colab'
                                ? `/gestionpersonas/personas-colaboradoras/editarColaborador/${p.Id}`
                                : `/gestionpersonas/personas/editarPersona/${p.Id}`
                              }
                            />
                          </TableCell>
                          )}
                        </StyledTableRow>
                      );
                    })}
                    {personasVacaciones.length === 0 && (
                      <StyledTableRow>
                        <TableCell colSpan={rolAdminExtendido ? 6 : 5} align="center">
                          <span className="text-gray-500">No se encontraron personas</span>
                        </TableCell>
                      </StyledTableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <div className="flex items-center justify-between w-[98%] mt-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-[rgba(0,60,102,0.8)] px-2 py-1">
                  Registros a mostrar
                </span>
                <Select
                  value={registrosPorPagVacaciones}
                  onChange={handleChangeRegistrosPorPagVacaciones}
                  size="small"
                  sx={{ width: 70, height: 30, fontSize: '14px' }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </div>

              <ContenedorPaginado>
                <Pagination
                  count={metaVacaciones.last_page || 1}
                  page={metaVacaciones.current_page}
                  onChange={handleChangePageVacaciones}
                  color="primary"
                  shape="rounded"
                  boundaryCount={1}
                  siblingCount={0}
                />
              </ContenedorPaginado>

              <span className="font-bold text-base text-[rgba(0,60,102,0.8)]">
                Total registros: {metaVacaciones.total || 0}
              </span>
            </div>
          </>
        )}
      </div>

      {/* ===== Diálogo "Asignar Calendario" (origen :1206-1242) ===== */}
      <Dialog open={showAsignarCalendarioModal} onClose={() => setShowAsignarCalendarioModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#336385', color: 'white' }}>
          Asignar Calendario Laboral
        </DialogTitle>
        <DialogContent>
          <p className="mt-4 mb-4">
            Se asignará el calendario laboral a <strong>{personasSeleccionadas.length} persona(s)</strong> seleccionada(s).
          </p>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Calendario Laboral</InputLabel>
            <Select
              value={calendarioParaAsignar}
              onChange={(e) => setCalendarioParaAsignar(e.target.value)}
              label="Calendario Laboral"
            >
              <MenuItem value="">Sin asignar</MenuItem>
              {calendariosLaborales.map((cal) => (
                <MenuItem key={cal.Id} value={cal.Id}>
                  {cal.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setShowAsignarCalendarioModal(false)} sx={{ color: '#333' }}>
            Cancelar
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={handleAsignarCalendario}
            sx={{ backgroundColor: '#336385', '&:hover': { backgroundColor: '#27496d' } }}
          >
            Asignar
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* ===== Diálogo checkpoint del masivo (origen :1244-1274) ===== */}
      <Dialog open={!!previewAsignar} onClose={() => { if (!aplicandoAsignar) setPreviewAsignar(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#336385', color: 'white' }}>
          Confirmar asignación
        </DialogTitle>
        <DialogContent>
          <p className="mt-4 mb-2">
            Se asignará el calendario laboral a <strong>{previewAsignar?.recuento} persona(s)</strong>.
          </p>
          {previewAsignar?.fuera_de_ambito?.length > 0 && (
            <p style={{ color: '#b71c1c', marginTop: 8 }}>
              <strong>{previewAsignar.fuera_de_ambito.length}</strong> destinatario(s) están fuera de tu ámbito de gestión y no se pueden asignar. Quítalos de la selección antes de aplicar.
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setPreviewAsignar(null)} disabled={aplicandoAsignar} sx={{ color: '#333' }}>
            Cancelar
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={handleConfirmarAsignar}
            disabled={aplicandoAsignar || (previewAsignar?.fuera_de_ambito?.length > 0)}
            sx={{ backgroundColor: '#336385', '&:hover': { backgroundColor: '#27496d' } }}
          >
            {aplicandoAsignar ? 'Aplicando…' : 'Aplicar'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
